import React from "react";
import {
  Box,
  VStack,
  HStack,
  Card,
  CardHeader,
  CardBody,
  Text,
  Switch,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  Select,
  Alert,
  AlertIcon,
  Heading,
  Divider,
  SimpleGrid,
} from "@chakra-ui/react";
import { useInstitutionConfig } from "./InstitutionConfigContext";

export default function GeneralSettingsTab() {
  const {
    formData,
    institutionConfig,
    decodedInstitution,
    institution,
    updating,
    updateConfig,
    refetchInstitutionConfig,
    handleUpdateField,
    toast,
  } = useInstitutionConfig();

  return (
    <VStack spacing={8} align="stretch">
      {/* Institution Details */}
      <Card bg="gray.800" borderColor="gray.600">
        <CardHeader>
          <Heading size="md" color="white">Institution Details</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <FormControl>
              <FormLabel color="white">Institution Name</FormLabel>
              <Input
                value={formData.institution || ""}
                isReadOnly
                bg="gray.700"
                borderColor="gray.600"
                color="gray.300"
              />
            </FormControl>
            <FormControl>
              <FormLabel color="white">Timezone</FormLabel>
              <Select
                value={formData.timezone || ""}
                onChange={(e) => handleUpdateField("timezone", e.target.value)}
                bg="gray.700"
                borderColor="gray.600"
                color="white"
              >
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
              </Select>
            </FormControl>
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Subscription & Scenario Access Management */}
      <Card bg="gray.800" borderColor="gray.700" borderWidth="1px">
        <CardHeader>
          <Heading size="md" color="white">Subscription & Scenario Access Management</Heading>
          <Text fontSize="sm" color="gray.400" mt={2}>
            Control scenario generator access and seat management for this institution
          </Text>
        </CardHeader>
        <CardBody>
          <VStack spacing={6} align="stretch">
            {/* Subscription Tier */}
            <FormControl>
              <FormLabel color="white">Subscription Tier</FormLabel>
              <Select
                value={institutionConfig?.subscriptionTier || 'individual'}
                onChange={async (e) => {
                  try {
                    if (!institutionConfig?.id) {
                      toast({
                        title: "Error",
                        description: "Institution configuration not found. Please refresh the page.",
                        status: "error",
                        duration: 5000,
                        isClosable: true,
                      });
                      return;
                    }
                    await updateConfig({
                      institution: decodedInstitution,
                      configurationUpdates: { subscriptionTier: e.target.value },
                      reason: 'Updated subscription tier via dropdown'
                    });
                    await refetchInstitutionConfig();
                    toast({
                      title: 'Subscription Tier Updated',
                      description: `Changed to ${e.target.value}`,
                      status: 'success',
                      duration: 3000,
                      isClosable: true,
                    });
                  } catch (error) {
                    toast({
                      title: 'Error',
                      description: error.message || 'Failed to update subscription tier',
                      status: 'error',
                      duration: 5000,
                      isClosable: true,
                    });
                  }
                }}
                bg="gray.900"
                borderColor="gray.600"
                color="white"
                isDisabled={updating}
              >
                <option value="individual">Individual (Personal Institution)</option>
                <option value="institutional">Institutional (Multi-User)</option>
              </Select>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Individual: Single user with personal institution | Institutional: Multiple users with seat management
              </Text>
            </FormControl>

            {/* Institutional Tier: Seat Management */}
            {institutionConfig?.subscriptionTier === 'institutional' && (
              <>
                <Divider borderColor="gray.600" />
                <Heading size="sm" color="white">Seat Management</Heading>

                <SimpleGrid columns={3} spacing={4}>
                  <Box p={4} bg="blue.900" bgOpacity="0.3" borderRadius="md">
                    <Text fontSize="sm" color="gray.400">Purchased Seats</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="white">{institutionConfig?.purchasedSeats || 0}</Text>
                  </Box>
                  <Box p={4} bg="green.900" bgOpacity="0.3" borderRadius="md">
                    <Text fontSize="sm" color="gray.400">Used Seats</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="white">{institutionConfig?.usedSeats || 0}</Text>
                  </Box>
                  <Box p={4} bg="purple.900" bgOpacity="0.3" borderRadius="md">
                    <Text fontSize="sm" color="gray.400">Available Seats</Text>
                    <Text fontSize="2xl" fontWeight="bold" color="white">
                      {(institutionConfig?.purchasedSeats || 0) - (institutionConfig?.usedSeats || 0)}
                    </Text>
                  </Box>
                </SimpleGrid>

                <FormControl>
                  <FormLabel color="white">Total Student Enrollment Limit</FormLabel>
                  <NumberInput
                    value={institutionConfig?.purchasedSeats || 0}
                    min={0}
                  >
                    <NumberInputField
                      bg="gray.900"
                      borderColor="gray.600"
                      color="white"
                      onBlur={async (e) => {
                        try {
                          if (!institutionConfig?.id) {
                            toast({
                              title: "Error",
                              description: "Institution configuration not found. Please refresh the page.",
                              status: "error",
                              duration: 5000,
                              isClosable: true,
                            });
                            return;
                          }
                          const newValue = parseInt(e.target.value) || 0;
                          await updateConfig({
                            institution: decodedInstitution,
                            configurationUpdates: { purchasedSeats: newValue },
                            reason: 'Updated student enrollment limit'
                          });
                          await refetchInstitutionConfig();
                          toast({
                            title: 'Student Enrollment Limit Updated',
                            status: 'success',
                            duration: 3000,
                            isClosable: true,
                          });
                        } catch (error) {
                          toast({
                            title: 'Error',
                            description: error.message || 'Failed to update student enrollment limit',
                            status: 'error',
                            duration: 5000,
                            isClosable: true,
                          });
                        }
                      }}
                    />
                  </NumberInput>
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Maximum number of STUDENTS who can be enrolled at this institution. This does NOT affect instructor scenario generator access.
                  </Text>
                </FormControl>

                {/* Warning if over capacity */}
                {(institutionConfig?.usedSeats || 0) > (institutionConfig?.purchasedSeats || 0) && (
                  <Alert status="warning">
                    <AlertIcon />
                    <Box>
                      <Text fontWeight="bold" color="white">Seat capacity exceeded!</Text>
                      <Text fontSize="sm" color="white">
                        You have {institutionConfig.usedSeats} students enrolled but only {institutionConfig.purchasedSeats} seats available.
                        Please increase the student enrollment limit or remove students.
                      </Text>
                    </Box>
                  </Alert>
                )}
              </>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Additional Settings */}
      <Card bg="gray.800" borderColor="gray.600">
        <CardHeader>
          <Heading size="md" color="white">Additional Settings</Heading>
        </CardHeader>
        <CardBody>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <FormControl>
              <HStack justify="space-between">
                <FormLabel color="white" mb={0}>Require Preceptor Approval</FormLabel>
                <Switch
                  isChecked={formData.requirePreceptorApproval || false}
                  onChange={(e) => handleUpdateField("requirePreceptorApproval", e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>

            <FormControl>
              <HStack justify="space-between">
                <FormLabel color="white" mb={0}>Access Code System Enabled</FormLabel>
                <Switch
                  isChecked={formData.accessCodeSystemEnabled || false}
                  onChange={(e) => handleUpdateField("accessCodeSystemEnabled", e.target.checked)}
                  colorScheme="blue"
                />
              </HStack>
            </FormControl>
          </SimpleGrid>
        </CardBody>
      </Card>
    </VStack>
  );
}
